/*
 * Studio Rivello theme behaviour. Ported from the Next.js site (lib/visitor.ts, lib/affiliate.ts).
 * - Routes every Amazon button to the visitor's own store, with that store's tag.
 * - Publishes an `affiliate_click` custom event (Shopify Customer Events → GA4 / TikTok).
 * - Category filter chips, gallery dots, sticky Amazon bar, click-to-load TikTok.
 */
(() => {
  "use strict";

  /* ---------- Store routing ---------- */

  const DOMAINS = {
    us: "amazon.com",
    uk: "amazon.co.uk",
    de: "amazon.de",
    fr: "amazon.fr",
    it: "amazon.it",
    es: "amazon.es",
    nl: "amazon.nl",
  };

  // Next stores to try when a visitor's own store has no tag or no link. EU stores ship across the EU.
  const FALLBACKS = {
    us: [],
    uk: [],
    de: ["nl", "fr", "it", "es"],
    nl: ["de", "fr", "it", "es"],
    fr: ["de", "it", "es", "nl"],
    it: ["de", "fr", "es", "nl"],
    es: ["de", "fr", "it", "nl"],
  };

  const STORE_BY_TIMEZONE = {
    "Europe/London": "uk", "Europe/Dublin": "uk", "Europe/Belfast": "uk", "Europe/Jersey": "uk",
    "Europe/Guernsey": "uk", "Europe/Isle_of_Man": "uk", "Europe/Gibraltar": "uk",
    "Europe/Amsterdam": "nl",
    "Europe/Berlin": "de", "Europe/Busingen": "de", "Europe/Vienna": "de",
    "Europe/Paris": "fr", "Europe/Luxembourg": "fr", "Europe/Monaco": "fr",
    "Europe/Rome": "it", "Europe/Vatican": "it", "Europe/San_Marino": "it",
    "Europe/Madrid": "es", "Atlantic/Canary": "es", "Africa/Ceuta": "es",
    "Europe/Lisbon": "es", "Atlantic/Madeira": "es", "Atlantic/Azores": "es",
  };

  const tags = (window.Rivello && window.Rivello.tags) || {};
  const OVERRIDE_KEY = "sr_store";

  // For testing: ?store=de acts like a visitor of that store for this session (?store=auto clears it).
  function storeOverride() {
    try {
      const param = new URLSearchParams(location.search).get("store");
      if (param === "auto") sessionStorage.removeItem(OVERRIDE_KEY);
      else if (param && DOMAINS[param]) sessionStorage.setItem(OVERRIDE_KEY, param);
      const stored = sessionStorage.getItem(OVERRIDE_KEY);
      return stored && DOMAINS[stored] ? stored : null;
    } catch {
      return null;
    }
  }

  function detectMarketplace() {
    const override = storeOverride();
    if (override) return override;
    let zone = "";
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {}
    const language = (navigator.language || "").toLowerCase();
    if (zone === "Europe/Brussels") return language.startsWith("fr") ? "fr" : "nl";
    if (zone === "Europe/Zurich") return language.startsWith("fr") ? "fr" : language.startsWith("it") ? "it" : "de";
    if (STORE_BY_TIMEZONE[zone]) return STORE_BY_TIMEZONE[zone];
    if (zone.startsWith("Europe/")) return "de";
    return "us";
  }

  function parseUrl(value) {
    try {
      return new URL(value);
    } catch {
      return null;
    }
  }

  function isStoreHost(url, marketplace) {
    const domain = DOMAINS[marketplace];
    return url.hostname === domain || url.hostname.endsWith("." + domain);
  }

  function linkInStore(link, marketplace) {
    if (marketplace === "us") return parseUrl(link.amazonUrl);
    const value = String(link.marketplaceUrls[marketplace] || "").trim();
    if (!value) return null;
    if (/^[A-Z0-9]{10}$/i.test(value)) {
      return new URL("https://www." + DOMAINS[marketplace] + "/dp/" + value.toUpperCase());
    }
    const url = parseUrl(value);
    return url && isStoreHost(url, marketplace) ? url : null; // wrong-store URLs are ignored
  }

  function buildAmazonUrl(link, locale) {
    let chosen = { url: parseUrl(link.amazonUrl), marketplace: "us" };
    for (const marketplace of [locale, ...(FALLBACKS[locale] || [])]) {
      if (!tags[marketplace]) continue;
      const url = linkInStore(link, marketplace);
      if (url) {
        chosen = { url, marketplace };
        break;
      }
    }
    if (!chosen.url) return { href: link.amazonUrl, marketplace: "us" };
    const tag = tags[chosen.marketplace];
    // Short links (amzn.to) already carry a tag and can't take another one.
    if (tag && isStoreHost(chosen.url, chosen.marketplace)) chosen.url.searchParams.set("tag", tag);
    return { href: chosen.url.toString(), marketplace: chosen.marketplace };
  }

  function routeAmazonLinks() {
    const locale = detectMarketplace();
    document.querySelectorAll("a[data-amazon]").forEach((a) => {
      let marketplaceUrls = {};
      try {
        marketplaceUrls = JSON.parse(a.dataset.marketplaceUrls || "null") || {};
      } catch {}
      const { href, marketplace } = buildAmazonUrl({ amazonUrl: a.dataset.amazonUrl, marketplaceUrls }, locale);
      a.href = href;
      a.dataset.marketplace = marketplace;
    });
  }

  /* ---------- affiliate_click ---------- */

  // Subscribe to it in a custom pixel (Settings → Customer events); Shopify applies the visitor's consent.
  document.addEventListener("click", (event) => {
    const a = event.target.closest && event.target.closest("a[data-amazon]");
    if (!a) return;
    const payload = {
      product: a.dataset.product,
      category: a.dataset.category,
      placement: a.dataset.placement,
      marketplace: a.dataset.marketplace,
    };
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish("affiliate_click", payload);
      }
    } catch {}
  });

  /* ---------- Category filter ---------- */

  function initFilter(root) {
    const chips = [...root.querySelectorAll("[data-filter-tag]")];
    const items = [...root.querySelectorAll("li[data-tags]")];
    const count = root.querySelector("[data-filter-count]");
    const row = root.querySelector("[data-filter-chips]");
    const valid = new Set(chips.map((c) => c.dataset.filterTag).filter(Boolean));
    const ON = ["border-bright", "bg-bright", "font-medium", "text-ink"];
    const OFF = ["border-line", "text-muted", "hover:border-muted", "hover:text-bright"];

    function apply(tag) {
      let visible = 0;
      items.forEach((li) => {
        const show = !tag || li.dataset.tags.split(",").includes(tag);
        li.hidden = !show;
        if (show) visible++;
      });
      chips.forEach((chip) => {
        const on = chip.dataset.filterTag === (tag || "");
        chip.setAttribute("aria-pressed", String(on));
        chip.classList.remove(...(on ? OFF : ON));
        chip.classList.add(...(on ? ON : OFF));
        const n = chip.querySelector("[data-chip-count]");
        if (n) n.className = on ? "text-ink/60" : "text-muted";
      });
      if (count) count.textContent = visible + (visible === 1 ? " piece" : " pieces");

      // Keep the active chip visible in the swipeable row.
      const active = row && row.querySelector('[aria-pressed="true"]');
      if (row && active) {
        const left = active.offsetLeft - row.offsetLeft;
        const right = left + active.offsetWidth;
        if (left < row.scrollLeft || right > row.scrollLeft + row.clientWidth) row.scrollTo({ left: left - 20 });
      }
    }

    function select(tag) {
      const url = new URL(location.href);
      if (tag) url.searchParams.set("tag", tag);
      else url.searchParams.delete("tag");
      history.replaceState(history.state, "", url);
      apply(tag);
    }

    chips.forEach((chip) =>
      chip.addEventListener("click", () => {
        const tag = chip.dataset.filterTag;
        const current = new URLSearchParams(location.search).get("tag");
        select(tag && tag === current ? "" : tag);
      }),
    );

    const requested = new URLSearchParams(location.search).get("tag");
    if (requested && valid.has(requested)) apply(requested);
  }

  /* ---------- Gallery dots ---------- */

  function initGallery(root) {
    const track = root.querySelector("[data-gallery-track]");
    const dots = [...root.querySelectorAll("[data-gallery-dot]")];
    if (!track || dots.length < 2) return;
    const setActive = (index) => dots.forEach((dot, i) => dot.setAttribute("aria-current", String(i === index)));
    track.addEventListener("scroll", () => setActive(Math.round(track.scrollLeft / track.clientWidth)), { passive: true });
    dots.forEach((dot, i) =>
      dot.addEventListener("click", () => {
        const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
        track.scrollTo({ left: i * track.clientWidth, behavior: reduce ? "auto" : "smooth" });
      }),
    );
  }

  /* ---------- Sticky Amazon bar ---------- */

  function initStickyCta(bar) {
    const target = document.getElementById(bar.dataset.stickyCta);
    const footer = document.querySelector("footer");
    if (!target || !("IntersectionObserver" in window)) return;
    const inView = { cta: true, footer: false };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === target) inView.cta = entry.isIntersecting;
        else inView.footer = entry.isIntersecting;
      });
      const visible = !inView.cta && !inView.footer;
      bar.inert = !visible;
      bar.classList.toggle("translate-y-full", !visible);
      bar.classList.toggle("translate-y-0", visible);
    });
    observer.observe(target);
    if (footer) observer.observe(footer);
  }

  /* ---------- TikTok, click to load ---------- */

  function initTikTok(box) {
    const button = box.querySelector("button");
    if (!button) return;
    button.addEventListener("click", () => {
      const iframe = document.createElement("iframe");
      iframe.src = "https://www.tiktok.com/player/v1/" + encodeURIComponent(box.dataset.tiktok) + "?autoplay=1&rel=0&description=0&music_info=0";
      iframe.title = box.dataset.tiktokTitle || "TikTok video";
      iframe.allow = "autoplay; encrypted-media; fullscreen; picture-in-picture";
      iframe.className = "absolute inset-0 size-full border-0";
      box.replaceChildren(iframe);
    });
  }

  /* ---------- Homepage browser: filter pills + search ---------- */

  function initBrowser(root) {
    const pills = [...root.querySelectorAll("[data-browse-filter]")];
    const search = root.querySelector("[data-browse-search]");
    const rows = root.querySelector("[data-browse-rows]");
    const results = root.querySelector("[data-browse-results]");
    const count = root.querySelector("[data-browse-count]");
    const empty = root.querySelector("[data-browse-empty]");
    const items = [...root.querySelectorAll("[data-browse-item]")];
    if (!rows || !results) return;

    const ON = ["border-bright", "bg-bright", "font-medium", "text-ink"];
    const OFF = ["border-line", "text-muted", "hover:border-muted", "hover:text-bright"];
    let filter = "all";

    function apply() {
      const term = (search?.value || "").trim().toLowerCase();
      const browsing = filter !== "all" || term !== "";
      rows.hidden = browsing;
      results.hidden = !browsing;
      if (!browsing) return;

      let visible = 0;
      items.forEach((li) => {
        const matchesFilter =
          filter === "all" ||
          (filter === "trending" && li.dataset.featured === "true") ||
          (filter === "new" && li.dataset.new === "true");
        const matchesTerm = !term || (li.dataset.text || "").includes(term);
        const show = matchesFilter && matchesTerm;
        li.hidden = !show;
        if (show) visible++;
      });
      if (count) count.textContent = visible + (visible === 1 ? " piece" : " pieces");
      if (empty) empty.hidden = visible !== 0;
    }

    pills.forEach((pill) =>
      pill.addEventListener("click", () => {
        filter = pill.dataset.browseFilter;
        pills.forEach((p) => {
          const on = p === pill;
          p.setAttribute("aria-pressed", String(on));
          p.classList.remove(...(on ? OFF : ON));
          p.classList.add(...(on ? ON : OFF));
        });
        apply();
      }),
    );
    search?.addEventListener("input", apply);
  }

  /* ---------- Cookie settings ---------- */

  function initCookieSettings(button) {
    // Only shown when Shopify's cookie banner is enabled (Settings → Customer privacy).
    const banner = window.privacyBanner;
    if (!banner || typeof banner.showPreferences !== "function") return;
    button.hidden = false;
    button.addEventListener("click", () => banner.showPreferences());
  }

  function init() {
    routeAmazonLinks();
    document.querySelectorAll("[data-filter]").forEach(initFilter);
    document.querySelectorAll("[data-browser]").forEach(initBrowser);
    document.querySelectorAll("[data-gallery]").forEach(initGallery);
    document.querySelectorAll("[data-sticky-cta]").forEach(initStickyCta);
    document.querySelectorAll("[data-tiktok]").forEach(initTikTok);
    // The privacy banner script can load after this one.
    const cookieButton = document.querySelector("[data-cookie-settings]");
    if (cookieButton) {
      initCookieSettings(cookieButton);
      window.addEventListener("load", () => cookieButton.hidden && initCookieSettings(cookieButton));
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
